/**
 * RowLens — one row of the table drawn as the graph it becomes: the values its cells
 * yield, the cells they came from, and the link the row lands in (with how many rows
 * share that link). The investigator steps through rows to see the recipe applied,
 * not described. Pure: everything comes in as props.
 */

import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { RowExplanation } from '@/services/api/structuredIngest'
import { RecipeGraph } from './RecipeGraph'
import { graphFromRow } from './recipe-graph-data'

export interface RowLensProps {
  explanation: RowExplanation | null
  loading: boolean
  /** 0-based row shown (controlled). */
  position: number
  total: number
  onPositionChange: (position: number) => void
  /** The item or link under inspection — node ids are the recipe's entity names. */
  selectedId?: string | null
  onSelect?: (id: string | null) => void
  className?: string
}

/** Below `md` a note takes its own line, where a leading separator would dangle. */
const NOTE = 'text-muted-foreground max-md:basis-full'
const DOT = (
  <span aria-hidden className="max-md:hidden">
    ·{' '}
  </span>
)

export function RowLens({
  explanation,
  loading,
  position,
  total,
  onPositionChange,
  selectedId,
  onSelect,
  className,
}: RowLensProps) {
  const { t } = useTranslation()
  const graph = useMemo(() => (explanation ? graphFromRow(explanation, t) : null), [explanation, t])
  const [draft, setDraft] = useState(String(position + 1))
  useEffect(() => setDraft(String(position + 1)), [position])
  const jump = () => {
    const wanted = Number.parseInt(draft, 10)
    if (Number.isFinite(wanted))
      onPositionChange(Math.min(Math.max(wanted - 1, 0), Math.max(total - 1, 0)))
    else setDraft(String(position + 1))
  }
  const stamp = explanation?.timestamp ? explanation.timestamp.replace('T', ' ').slice(0, 16) : null
  const missing =
    explanation?.entities.filter(e => e.enabled !== false && e.missing).map(e => e.name) ?? []
  const outside =
    explanation?.relationships.filter(r => r.enabled !== false && !r.in_scope).map(r => r.type) ??
    []

  return (
    <div className={className}>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <Button
          type="button"
          size="icon-sm"
          variant="outline"
          className="max-md:size-9"
          disabled={position <= 0}
          onClick={() => onPositionChange(position - 1)}
          aria-label={t('tableMapping.lens.previous')}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="flex items-center gap-1">
          {t('tableMapping.lens.row')}
          <Input
            className="w-16 text-center tabular-nums md:h-7"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={jump}
            onKeyDown={e => {
              if (e.key === 'Enter') jump()
            }}
            aria-label={t('tableMapping.lens.rowNumber')}
          />
          {t('tableMapping.lens.of', { total })}
        </span>
        <Button
          type="button"
          size="icon-sm"
          variant="outline"
          className="max-md:size-9"
          disabled={position >= total - 1}
          onClick={() => onPositionChange(position + 1)}
          aria-label={t('tableMapping.lens.next')}
        >
          <ChevronRight className="size-4" />
        </Button>
        {stamp && <span className="text-muted-foreground">{stamp}</span>}
        {loading && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
        {missing.length > 0 && (
          <span className={NOTE}>
            {DOT}
            {t('tableMapping.lens.missing', { names: missing.join(', ') })}
          </span>
        )}
        {outside.length > 0 && (
          <span className={NOTE}>
            {DOT}
            {t('tableMapping.lens.outside', { types: outside.join(', ') })}
          </span>
        )}
      </div>
      {graph ? (
        <RecipeGraph
          data={graph}
          selectedId={selectedId ?? null}
          onSelect={onSelect}
          className="h-80 md:h-60"
        />
      ) : (
        <div className="flex h-80 items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground md:h-60">
          {loading ? t('tableMapping.lens.reading') : t('tableMapping.lens.pick')}
        </div>
      )}
    </div>
  )
}
